import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@app/services/auth.service';
import { NotifyService } from '@app/services/notify.service';
import { UpdateView } from '@common/views';
import { BenefitDetail } from '@models/benefits/benefit-detail.model';
import { Benefit } from '@models/benefits/benefit.model';

@Component({
    templateUrl: './plan-update.template.html',
    styleUrls: ['./plan-update.style.scss'],
})
export class PlanUpdateView extends UpdateView {
    benefit: Benefit = new Benefit();
    existingBenefitDetails: BenefitDetail[] = [];
    benefitPlanId: number;
    isLoading = false;

    constructor(
        public auth: AuthService,
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService
    ) {
        super();

        this.benefitPlanId = this.route.snapshot.params.benefitPlan;

        if (this.route.snapshot.data.models) {
            this.benefit = this.route.snapshot.data.models.get('benefit');
            this.existingBenefitDetails = [...this.benefit.benefitDetails];
        }
    }

    onSave(): void {
        this.isLoading = true;
        this.benefit.benefitPlanId = this.benefitPlanId;
        this.benefit
            .param('company', this.auth.company.id)
            .param('benefitPlan', this.benefit.benefitPlanId)
            .save()
            .then(() => this.doDeletedChildren(this.benefit))
            .then(() => this.doModifiedAndNewChildren(this.benefit))
            .then((benefit) => {
                this.notify.success('Benefit updated');
                this.router.navigate(['benefits', 'plans', benefit.benefitPlanId]);
            })
            .catch((err) => {
                this.notify.error(err.message || 'Could not update benefit.');
            });
    }

    /**
     * Perform a delete rexist on benefitDetails that have
     * been removed from the benefitDetail list
     * @param  {Benefit}          benefit [description]
     * @return {Promise<Benefit>}         [description]
     */
    doDeletedChildren(benefit: Benefit): Promise<Benefit> {
        return new Promise<Benefit>((resolve, reject) => {
            // Find benefit details that no longer exist
            this.existingBenefitDetails = this.existingBenefitDetails.filter((existingBenefitDetail) => {
                const isBenefitRemaining = this.benefit.benefitDetails.find(
                    (benefitDetail) => benefitDetail.id !== null && existingBenefitDetail.id === benefitDetail.id
                );
                return !isBenefitRemaining;
            });

            // Remove the no longer existing benefit details
            Promise.all(
                this.existingBenefitDetails.map((existingBenefitDetail) =>
                    existingBenefitDetail
                        .param('company', this.auth.company.id)
                        .param('benefitPlan', this.benefit.benefitPlanId)
                        .param('benefit', this.benefit.id)
                        .delete()
                )
            )
                .then(() => resolve(benefit))
                .catch((err) => reject(err));
        });
    }

    /**
     * Save modified or new benefit details
     * @param  {Benefit}          benefit [description]
     * @return {Promise<Benefit>}         [description]
     */
    doModifiedAndNewChildren(benefit: Benefit): Promise<Benefit> {
        return new Promise<Benefit>((resolve, reject) => {
            Promise.all(
                this.benefit.benefitDetails.map((benefitDetail) => {
                    benefitDetail.benefitId = this.benefit.id;
                    benefitDetail
                        .param('company', this.auth.company.id)
                        .param('benefitPlan', this.benefit.benefitPlanId)
                        .param('benefit', this.benefit.id)
                        .save();
                })
            )
                .then(() => resolve(benefit))
                .catch((err) => reject(err));
        });
    }
}
